import * as React from 'react';

import { Button, Typography } from '@mui/material';
//import { Box, Button, Typography } from '@mui/material';
import { PageSection } from '../subs/PageSection';
import { CircularProgressWithLabel } from '../subs/CircularProgressWithLabel';
import { VerticalButtonPanel } from '../subs/ButtonPanel';

//import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { InfoPanel } from '../subs/InfoPanel';
import { Page } from '../subs/Page';
import { UserContext } from '../UserProvider';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../contexts/SnackBarContext';

import { ButtonLink } from '../subs/ButtonLink';
import { Workshop, WorkshopStatus } from '../WorkshopPage';
import { formatDateString } from '../Web';
import { getWorkshopUsers } from '../types/WorkshopUser';
import { getUpcomingWorkshop } from '../types/Workshop';
import { getCompanyWorkshopProducts, WorkshopProduct } from '../types/WorkshopProducts';
import { WorkshopResultCounters, getResultCounters, GetEmptyResultCounters } from '../types/WorkshopResult';
import { WorkshopProductCombination, getProductCombinations } from '../types/WorkshopProductCombination';

const calcTestPercentage = (totalProductCount: number,
    { testsPassed, testsFailed, testsWarning, testsNotCompleted }: WorkshopResultCounters,
) => {
    const testCompleted = testsPassed + testsFailed + testsWarning;
    const totalTestCases = testsNotCompleted + testCompleted;
   

    return (testCompleted / totalTestCases) / totalProductCount;
}

export const Dashboard = () => {
    const { showSnackbar } = useSnackbar();
    const userContext = React.useContext(UserContext);
    const [msgText, setMsgText] = React.useState<string | null>(null);
    const [msgSeverity, setMsgSeverity] = React.useState<AlertProps['severity']>('error');
    const [activeWorkshop, setActiveWorkshop] = React.useState<Workshop | undefined>();
    const [userIsRegistered, setUserIsRegistered] = React.useState<boolean>(false);

    const [userActiveWorkshopProducts, setUserActiveWorkshopProducts] = React.useState<WorkshopProduct[]>();
    const [productCompletionPercentage, setProductCompletionPercentage] = React.useState<number>(0);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const msgTextLoc = msgText ?? null;

        if (!msgTextLoc || msgTextLoc === '') return;
        setMsgText(null);
        showUserFeedback(msgTextLoc, msgSeverity);
    }, [showUserFeedback, msgText, msgSeverity]);

    React.useEffect(()=> {
        async function fetch() {
            try {
                const userWorkshopsRequest = await getUpcomingWorkshop(new AbortController());
                
                if(userWorkshopsRequest.failed) {
                    setMsgSeverity('error');
                    setMsgText(userWorkshopsRequest.errorText);
                    return;
                }

                const currentWorkshop = userWorkshopsRequest.result;
                if (!currentWorkshop || currentWorkshop.id === 0 || currentWorkshop.status === WorkshopStatus.Invisible) {
                    setActiveWorkshop(undefined);
                    return;
                }

                setActiveWorkshop(currentWorkshop);
                //getWorkshopUsers
                const getWorkshopUsersRequest = await getWorkshopUsers(currentWorkshop.id, new AbortController());
                if (getWorkshopUsersRequest.failed) {
                    setMsgSeverity('error');
                    setMsgText(getWorkshopUsersRequest.errorText);
                    return;
                }
                const isUserRegistered = getWorkshopUsersRequest.result.some(u => u.userId === userContext.user.id && u.workshopId === currentWorkshop.id);
                setUserIsRegistered(isUserRegistered);

                const userWorkshopProducts = await getCompanyWorkshopProducts(userContext.user.companyName!!, currentWorkshop.id, new AbortController());
                setUserActiveWorkshopProducts(userWorkshopProducts);

            } catch(error) {
                setMsgSeverity('error');
                setMsgText(String(error));
                return;
            }   
        }

        fetch();
    }, [userContext]);

    React.useEffect(() => {
        const fetch = async () => {
            if(!userActiveWorkshopProducts || userActiveWorkshopProducts.length === 0)
                return;
            if (!activeWorkshop || activeWorkshop.id === 0 || activeWorkshop.id === undefined) return;
            //       const userWSProdConfigRequest = await getProductCombinations(userActiveWorkshopProducts.map(uwp => uwp.id), new AbortController());
            const userWSProdConfigPromises = userActiveWorkshopProducts.map(uwp => getProductCombinations(uwp.id, new AbortController()));
            const userWSProdConfigResults = await Promise.all(userWSProdConfigPromises);
            let consumerSubscriberCount = 0;        // a product can be both consumer and provider, so we count both roles as weighted towards the total number of products
            let testPercentage = 0;

            let someFailed = userWSProdConfigResults.find(response => response.failed);
            if ((someFailed)) {
                setMsgSeverity('error');
                someFailed.errorText && setMsgText(someFailed.errorText);
                return;
            }

            let userWSProdConfigs: WorkshopProductCombination[] = [];
            userWSProdConfigResults.forEach(res => userWSProdConfigs = [...userWSProdConfigs, ...res.result]);

            userWSProdConfigs.forEach((uwpc) => {
                if (uwpc.isConsumer) consumerSubscriberCount++;
                if (uwpc.isProvider) consumerSubscriberCount++;
            });

            const resultCountersRequest = await getResultCounters(activeWorkshop.id, new AbortController());

            if (resultCountersRequest.failed) {
                setMsgSeverity('error');
                setMsgText(resultCountersRequest.errorText);
                return;
            }
            const resultCounters: WorkshopResultCounters[] = resultCountersRequest.result;
            let resultCounter: WorkshopResultCounters = GetEmptyResultCounters();

            userWSProdConfigs.forEach((uwpc) => {
                resultCounter = resultCounters.find(rc => uwpc.workshopProductId === rc.workshopProductId && rc.testCombinationId === 0
                    && rc.isConsumer === false && rc.isProvider === false) ?? GetEmptyResultCounters();
                testPercentage += calcTestPercentage(consumerSubscriberCount, resultCounter);
            });
            if (testPercentage > 1) {
                testPercentage = 1;
            }
            setProductCompletionPercentage(testPercentage);
        }
        fetch();
    }, [userActiveWorkshopProducts, activeWorkshop]);

    const activeUserProducts = userActiveWorkshopProducts?.length ?? 0;
    return <>
        <Page heading={`Welcome back, ${userContext.user.name}!`}>
            <PageSection>
                {/*     <InfoPanel>
                    {[<Typography>{"From here, you can manage your participation within the interoperability workshop (IOP) as follows:"}</Typography>]}
                </InfoPanel>

                <Box textAlign={"right"}>
                    <VerticalButtonPanel heading={"Are you new to our application?"}>
                        {[<Button style={{ color: 'white' }} startIcon={<PictureAsPdfIcon />} href='https://iopworkshop.opcfoundation.org/IOPWorkshopApplicationUserGuide.pdf'>{"View documentation"}</Button>]}
                    </VerticalButtonPanel>
                </Box>*/}
            </PageSection>
             
            <PageSection>
                <CurrentWorkshopPanel workshop={activeWorkshop} />
                <CircularProgressWithLabel value={Math.round(productCompletionPercentage * 100)} caption={"of your products tested"} subcaption={"total: " + activeUserProducts + " products included"}/>
            </PageSection>

            <PageSection>
                <VerticalButtonPanel heading='Registration via the OPC Foundation Website'>
                    <Button disabled={userIsRegistered} sx={{ color: userIsRegistered ? 'text.disabled' : 'common.white' }} startIcon={<EventAvailableIcon />}
                        target='_blank' href={'https://opcfoundation.org/events/'}>{"Register now"}
                    </Button>
                </VerticalButtonPanel>
                <VerticalButtonPanel heading={"Conduct testing and feedback results"}>
                    
                    <ButtonLink startIcon={<AutorenewIcon />} to={"/TestingTab"}>{"Start testing"}</ButtonLink>
                </VerticalButtonPanel>
                <VerticalButtonPanel heading={"View and finalize test results"}>
                    
                    <ButtonLink startIcon={<VisibilityIcon />} to={"/Results"}>{"View the results"}</ButtonLink>
                </VerticalButtonPanel>
            </PageSection>
        </Page>
    </>
}

const CurrentWorkshopPanel = ({workshop}: {workshop?: Workshop}) => {
    if (workshop)
        // check the workshop dates to see it is currently active
        if (workshop.startDate && workshop.endDate
            && new Date(formatDateString(workshop.startDate) ?? 0) <= new Date()
            && new Date(formatDateString(workshop.endDate) ?? 0) >= new Date()) {
            return <InfoPanel>
                <Typography variant='body2'>{`${formatDateString(workshop.startDate)} - ${formatDateString(workshop.endDate)}`}</Typography>
                <Typography variant='h5'>{"Current Workshop event in " + workshop.location}</Typography>
            </InfoPanel>
        }
        else {
            return <InfoPanel>
                <Typography variant='body2'>{`${formatDateString(workshop.startDate)} - ${formatDateString(workshop.endDate)}`}</Typography>
                <Typography variant='h5'>{"Upcoming Workshop event in " + workshop.location}</Typography>
            </InfoPanel>
        }
    else
        return null;
}